import { useMemo } from 'react';

import operationTypes from 'constants/operationTypes';

import { HoldingClass, Operation } from 'types';

import { getTransactionAmount } from 'modules/transactions/pages/OperationDetails/utils/getTransactionAmount';
import { getTransactionSharesCount } from 'modules/transactions/utils/getTransactionSharesCount';
import { getPartnerTransactionsFromOperation } from '../utils/getPartnerTransactionsFromOperation';

type UseGetOperationDataProps = {
  partnerId: string | undefined;
  operation: Operation;
  nominalValue: number;
};

const alwaysExpandTypes: string[] = [
  operationTypes.SELL_PARTICIPATION,
  operationTypes.HERITAGE,
  operationTypes.DONATION,
  operationTypes.TRANSMISSION,
  operationTypes.ENDORSEMENT,
];

const excludedExpandTypes: string[] = [
  operationTypes.SPLIT,
  operationTypes.RECLASSIFICATION,
  operationTypes.RENUMERATION,
];

const emptyResult = {
  id: '',
  alias: '',
  type: '',
  date: '',
  comments: '',
  amount: 0,
  sharesCount: 0,
  className: '',
  from: 0,
  to: 0,
  partnerTransactions: [],
  showExpandButton: false,
};

export const useGetOperationData = ({
  partnerId,
  operation,
  nominalValue,
}: UseGetOperationDataProps) => {
  const data = useMemo(() => {
    if (!operation || !partnerId) return emptyResult;

    const partnerTransactions = getPartnerTransactionsFromOperation({
      operation,
      partnerId,
    });

    const amount = partnerTransactions.reduce((acc: number, transaction) => {
      const transactionSharesCount = getTransactionSharesCount(transaction);

      return (
        acc +
        getTransactionAmount({
          transaction,
          sharesCount: transactionSharesCount,
          nominalValue,
        })
      );
    }, 0);

    const sharesCount = partnerTransactions.reduce(
      (acc: number, transaction) =>
        acc + getTransactionSharesCount(transaction),
      0
    );

    const hasMultiple = partnerTransactions.length > 1;
    const mustExpand =
      alwaysExpandTypes.includes(operation.operationType) || hasMultiple;
    const cannotExpand = excludedExpandTypes.includes(operation.operationType);

    let className = '';
    let from = 0;
    let to = 0;

    if (!mustExpand && partnerTransactions.length === 1) {
      const { shareClass, shareFrom, shareTo } = partnerTransactions[0];
      className = (shareClass as HoldingClass)?.name || '';
      from = shareFrom || 0;
      to = shareTo || 0;
    }

    return {
      id: operation._id,
      alias: operation?.alias || '',
      type: operation.operationType || '',
      date: operation.date || '',
      comments: operation.comments || '',
      amount,
      sharesCount,
      className,
      from,
      to,
      partnerTransactions,
      showExpandButton: mustExpand && !cannotExpand,
    };
  }, [partnerId, operation, nominalValue]);

  return { data };
};
